import * as React from 'react';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';

import { Action, Dispatch } from 'redux';
import { DocumentHistoryState } from './document-history.reducer';
import { DocumentHistoryViewModel } from '@models/DocumentHistory';
import { documentHistoryService } from '@services/DocumentHistoryService';
import { documentService } from '@services/DocumentService';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum DocumentHistoryActionType {
  GetDocumentHistoryStart = 'GetDocumentHistoryStart',
  GetDocumentHistorySuccess = 'GetDocumentHistorySuccess',
  GetDocumentHistoryFailure = 'GetDocumentHistoryFailure',
  ResendDocumentStart = 'ResendDocumentStart',
  ResendDocumentSuccess = 'ResendDocumentSuccess',
  ResendDocumentFailure = 'ResendDocumentFailure'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

/********** Search **********/
export interface GetDocumentHistoryStartAction extends Action {
  readonly entityId: number;
  readonly type: DocumentHistoryActionType.GetDocumentHistoryStart;
}

export interface GetDocumentHistorySuccessAction extends Action {
  readonly result: DocumentHistoryViewModel[];
}

export interface GetDocumentHistoryFailureAction extends Action {
  readonly errorMessage: string;
  readonly type: DocumentHistoryActionType.GetDocumentHistoryFailure;
}

/********** Resend **********/
export interface ResendDocumentStartAction extends Action {}

export interface ResendDocumentSuccessAction extends Action {
  readonly id: number;
}

export interface ResendDocumentFailureAction extends Action {
  readonly errorMessage: string;
}

/********** Search **********/
export const getDocumentHistory = (entityId: number) => {
  return async (dispatch: Dispatch<DocumentHistoryState>) => {
    dispatch(getDocumentHistoryStart(entityId));
    let result: DocumentHistoryViewModel[];
    try {
      result = await documentHistoryService.getDocumentHistory(entityId);
      dispatch(getDocumentHistorySuccess(result));
    } catch (e) {
      dispatch(getDocumentHistoryFailure(e.toString()));
    }
  };
};

export function getDocumentHistoryStart(entityId: number): GetDocumentHistoryStartAction {
  return {
    entityId: entityId,
    type: DocumentHistoryActionType.GetDocumentHistoryStart
  };
}

export function getDocumentHistorySuccess(result: DocumentHistoryViewModel[]): GetDocumentHistorySuccessAction {
  return {
    result: result,
    type: DocumentHistoryActionType.GetDocumentHistorySuccess
  };
}

export function getDocumentHistoryFailure(errorMessage: string): GetDocumentHistoryFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to load document history. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: DocumentHistoryActionType.GetDocumentHistoryFailure
  };
}

/********** Resend **********/
export function resendDocument(id: number, entityId?: number, email?: string) {
  return async (dispatch: Dispatch<DocumentHistoryState>) => {
    dispatch(resendDocumentStart());
    try {
      await documentService.resendDocument(id, entityId, email);
      return dispatch(resendDocumentSuccess(id));
    } catch (e) {
      dispatch(resendDocumentFailure(e.toString()));
      return;
    }
  };
}

export function resendDocumentStart(): ResendDocumentStartAction {
  return {
    type: DocumentHistoryActionType.ResendDocumentStart
  };
}

export function resendDocumentSuccess(id: number): ResendDocumentSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Document Sent Successfully`} />,
    ToastOptions.Success
  );
  return {
    type: DocumentHistoryActionType.ResendDocumentSuccess,
    id: id
  };
}

export function resendDocumentFailure(errorMessage: string): ResendDocumentFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to resend document. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: DocumentHistoryActionType.ResendDocumentFailure
  };
}



// WEBPACK FOOTER //
// ./src/document-history/document-history.actions.tsx